import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CheckboxFormField, InputFormField } from "components/hookForm";
import { Button, Container, Divider, Toast as toast, TypoGraph } from "components";
import { getWhitelabelConfiguration, sendPaymentSubmittedEmailTest } from "api";
import styles from "./disbursementReminders.module.scss";

const BAR = "#0b1f4b";
const BUTTON = "#1a4fa0";

const PaymentSubmittedEmail = ({ companyId }: { companyId?: string }) => {
  const { getValues, setValue, watch } = useFormContext();
  const enabled = !!watch("paymentSubmittedEmailConfig.enabled");
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState(false);
  const barColor = watch("paymentSubmittedEmailConfig.barColor") || BAR;
  const buttonColor = watch("paymentSubmittedEmailConfig.buttonColor") || BUTTON;
  const email = watch("paymentSubmittedEmailConfig.email") || {};
  const companyName = watch("name") || "Company";
  const [headerImage, setHeaderImage] = useState("");
  const steps = String(email.steps || "")
    .split("\n")
    .map((step: string) => step.trim())
    .filter(Boolean);

  useEffect(() => {
    if (!companyId) {
      setHeaderImage("");
      return;
    }
    getWhitelabelConfiguration(companyId)
      .then((response) => setHeaderImage(response.data?.logoBase64 || ""))
      .catch(() => setHeaderImage(""));
  }, [companyId]);

  const sendTest = () => {
    if (!companyId || !recipient.trim()) {
      return;
    }
    setSending(true);
    sendPaymentSubmittedEmailTest(companyId, {
      recipient: recipient.trim(),
      email: getValues("paymentSubmittedEmailConfig.email") || {},
      barColor,
      buttonColor,
    })
      .then(() => toast({ title: "Test email sent", subTitle: recipient.trim() }))
      .catch(() => undefined)
      .finally(() => setSending(false));
  };

  return (
    <Container className={styles.section}>
      <Divider title="Payment submitted" />
      <CheckboxFormField
        name="paymentSubmittedEmailConfig.enabled"
        label="Send a custom payment submitted email"
      />
      <TypoGraph
        variant="body2"
        content="Off by default. When on, the person who submitted the payment gets this email instead of the standard one. The header uses the whitelabel logo, or the company name when there is no logo."
      />
      <TypoGraph
        variant="body2"
        content="Placeholders, replaced when the email is sent: {{userFirstName}} and {{userLastName}} (the user who created the payment), {{companyName}}, {{addressLine1}}, {{addressLine2}}, {{city}}, {{state}}, {{zipCode}} (the address on the payment), {{amount}} (dollars), {{itemId}}. A placeholder with no value is left blank."
      />
      {enabled && (
        <Container className={styles.reminder}>
          {companyId ? (
            <label className={styles.testRow}>
              Test recipient
              <input
                className={styles.testInput}
                type="email"
                value={recipient}
                placeholder="name@example.com"
                onChange={(event) => setRecipient(event.target.value)}
              />
            </label>
          ) : (
            <TypoGraph
              variant="body2"
              content="Save the company, then you can send a test."
            />
          )}
          <TypoGraph
            variant="body2"
            content="A test has no payment, so every placeholder except the company name is blank."
          />
          <div className={styles.colors}>
            <label>
              Header and footer
              <input
                className={styles.colorInput}
                type="color"
                value={barColor}
                aria-label="Payment submitted header and footer color"
                onChange={(event) =>
                  setValue("paymentSubmittedEmailConfig.barColor", event.target.value, {
                    shouldDirty: true,
                  })
                }
              />
            </label>
            <label>
              Button
              <input
                className={styles.colorInput}
                type="color"
                value={buttonColor}
                aria-label="Payment submitted button color"
                onChange={(event) =>
                  setValue("paymentSubmittedEmailConfig.buttonColor", event.target.value, {
                    shouldDirty: true,
                  })
                }
              />
            </label>
          </div>
          <InputFormField
            name="paymentSubmittedEmailConfig.email.opening"
            label="Opening"
            multiline
            rows={3}
            maxLength={2000}
            required
          />
          <InputFormField
            name="paymentSubmittedEmailConfig.email.heading"
            label="Heading"
            maxLength={500}
            required
          />
          <InputFormField
            name="paymentSubmittedEmailConfig.email.steps"
            label="Steps, one per line"
            multiline
            rows={4}
            maxLength={2000}
            required
          />
          <InputFormField
            name="paymentSubmittedEmailConfig.email.footnote"
            label="Footnote"
            maxLength={500}
          />
          <InputFormField
            name="paymentSubmittedEmailConfig.email.buttonLabel"
            label="Button label"
            maxLength={80}
            required
          />
          <TypoGraph
            variant="body2"
            content="The button is wording only. This email does not include a link."
          />
          <InputFormField
            name="paymentSubmittedEmailConfig.email.closing"
            label="Closing"
            multiline
            rows={3}
            maxLength={2000}
            required
          />
          <div className={styles.preview}>
            <div className={styles.header} style={{ background: barColor }}>
              {headerImage ? <img src={headerImage} alt="" /> : companyName}
            </div>
            <div className={styles.body}>
              <p>{email.opening}</p>
              <p className={styles.heading}>{email.heading}</p>
              {steps.length > 0 && (
                <ol>
                  {steps.map((step: string, index: number) => (
                    <li key={`${index}-${step}`}>{step}</li>
                  ))}
                </ol>
              )}
              {email.footnote && <p className={styles.footnote}>{email.footnote}</p>}
              <div className={styles.button} style={{ background: buttonColor }}>
                {email.buttonLabel || "Button"}
              </div>
              <p>{email.closing}</p>
            </div>
            <div className={styles.footer} style={{ background: barColor }} />
          </div>
          {companyId && (
            <Button
              type="button"
              variant="outlined"
              label={sending ? "Sending..." : "Send test"}
              disabled={sending || !recipient.trim()}
              onClick={sendTest}
            />
          )}
        </Container>
      )}
    </Container>
  );
};

export default PaymentSubmittedEmail;
